import { useCallback, useEffect, useRef, useState } from 'react'
import { KeyRound, ShieldAlert } from 'lucide-react'
import { adminMutate, apiGet, usePermissions } from './primitives.js'
import { useConfirm } from '../shell/confirm.js'
import { estErreurApi, fraicheurFr } from '../communication/lib.js'
import {
  etapeSuppression, GUIDES, ligneAge, messageApresEnregistrement, nomNouvelleCle, pastilleCle,
  phaseApresEnregistrement, SUIVI_PAS_MS,
  type EtatCle, type IntegrationACle,
} from './cle-acces-model.js'

/**
 * PANNEAU « CLÉ D'ACCÈS » — un par intégration qui s'ouvre par une clé
 * (décision de l'exploitant, 15/10/2026). Même composant partout : UniFi
 * Network, UniFi Protect, Home Assistant, UniFi Access, Skello, Shotgun.
 *
 * Ce qu'il garantit :
 *   · le champ est TOUJOURS vide à l'ouverture : aucune clé ne descend du
 *     serveur (l'API ne la renvoie jamais), il n'y a rien à préremplir ;
 *   · « Tester cette clé » l'essaie chez le fournisseur SANS l'enregistrer ;
 *   · « Enregistrer la clé » demande la confirmation forte (adminMutate
 *     rejoue la requête après ré-authentification) ;
 *   · le mode d'emploi dit l'ordre sûr — créer, tester, enregistrer, puis
 *     seulement supprimer l'ANCIENNE clé de Gestion chez le fournisseur ;
 *   · après l'enregistrement, l'écran RELIT l'état (au plus une minute) et
 *     n'invite à supprimer l'ancienne clé qu'une fois une lecture réussie
 *     avec la nouvelle (revue du 15/10/2026 : il le disait aussitôt, même
 *     d'une clé jamais acceptée).
 *
 * Essayer comme enregistrer demandent la confirmation forte : la permission
 * « modifier les intégrations » l'exige partout (catalogue des droits).
 *
 * Pourquoi des boutons et pas la Save Bar : remplacer une clé est un geste
 * ATOMIQUE et sensible (doctrine de shell/editing.tsx — « les actions
 * atomiques ne passent pas par la barre »). Une clé à moitié collée n'est pas
 * une « modification non enregistrée » à protéger d'une navigation : c'est un
 * secret qui ne doit pas s'attarder. Le champ se vide après l'enregistrement.
 */

export type { IntegrationACle } from './cle-acces-model.js'

interface EtatOuErreur { error: string; message?: string }

type Verdict = 'ACCEPTEE' | 'REFUSEE' | 'INJOIGNABLE' | 'INATTENDUE' | 'INCOMPLETE'
interface ResultatTest { verdict: Verdict; message: string }

const fmtDate = (iso: string) => new Date(iso).toLocaleString('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
})

function PastilleCle({ e }: { e: EtatCle }) {
  const p = pastilleCle(e)
  return <span className={`lc-pill ${p.classe}`} title={p.titre}>{p.texte}</span>
}

export function CleAccesPanel({ integration, onEnregistree, relire = 0 }: {
  integration: IntegrationACle
  onEnregistree?: () => void
  /**
   * Relire l'état SANS remonter le panneau : la page l'incrémente quand
   * l'adresse ou l'organisateur changent (ils conditionnent l'essai). Une clé
   * collée et testée mais pas encore enregistrée reste là ; seul son verdict
   * tombe si l'adresse ou l'organisateur ont changé (il valait pour l'ancien).
   */
  relire?: number
}) {
  const permissions = usePermissions()
  const peutModifier = permissions.permissions.includes('integration.write')
  const confirm = useConfirm()
  const [etat, setEtat] = useState<EtatCle | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  // Toujours vide au départ : la valeur ne vient JAMAIS du serveur.
  const [cle, setCle] = useState('')
  const [essai, setEssai] = useState<'repos' | 'en_cours'>('repos')
  const [resultat, setResultat] = useState<(ResultatTest & { pour: string }) | null>(null)
  const [envoi, setEnvoi] = useState<'repos' | 'envoi' | 'ok' | 'annule' | 'droits' | 'echec'>('repos')
  const [detailEchec, setDetailEchec] = useState<string | null>(null)
  // Instant de l'enregistrement : tant qu'il est posé, l'écran relit l'état.
  // `avant` : date de la clé remplacée. Tant que l'état relu la porte encore,
  // il parle de l'ANCIENNE clé — son « réussi » ne vaut rien pour la nouvelle.
  const [suivi, setSuivi] = useState<{ depuis: number; avant: string | null } | null>(null)
  const [maintenant, setMaintenant] = useState(() => Date.now())

  // Le dernier état lu, pour savoir si l'adresse a changé entre deux lectures.
  const etatLu = useRef<EtatCle | null>(null)
  const charger = useCallback(() => {
    void apiGet<EtatCle | EtatOuErreur>(`/api/v1/settings/integrations/${integration}/cle`)
      .then((j) => {
        if (estErreurApi(j)) { setErreur(j.message ?? 'Lecture impossible.'); return }
        setErreur(null)
        const avant = etatLu.current
        if (avant && (avant.adresse !== j.adresse || avant.organisateur !== j.organisateur)) setResultat(null)
        etatLu.current = j
        setEtat(j)
      })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [integration])
  useEffect(() => { charger() }, [charger, relire])

  /*
   * SUIVI APRÈS ENREGISTREMENT — toutes les 4 s, une minute au plus, jusqu'à
   * la première lecture (réussie ou non) avec la nouvelle clé. Le worker lit
   * en quelques secondes ; sans ce suivi, l'écran restait figé sur « pas
   * encore réussie » à côté d'un « supprimez l'ancienne clé ».
   */
  const phase = suivi && etat ? phaseApresEnregistrement(etat, maintenant - suivi.depuis, suivi.avant) : null
  const suiviActif = phase === 'attente'
  useEffect(() => {
    if (!suiviActif) return
    const t = window.setInterval(() => { setMaintenant(Date.now()); charger() }, SUIVI_PAS_MS)
    return () => window.clearInterval(t)
  }, [suiviActif, charger])

  const valeur = cle.trim()
  const tropCourte = valeur.length < 8
  // Le verdict ne vaut que pour la valeur essayée : on la retape, il tombe.
  const verdictCourant = resultat && resultat.pour === valeur ? resultat : null

  const tester = async () => {
    if (tropCourte) return
    setEssai('en_cours'); setEnvoi('repos')
    try {
      const r = await adminMutate<ResultatTest & { error?: string; message?: string }>(
        'POST', `/api/v1/settings/integrations/${integration}/cle/test`, { cle: valeur })
      if (r.status === 200) setResultat({ verdict: r.data.verdict, message: r.data.message, pour: valeur })
      // L'essai demande lui aussi la confirmation d'identité : l'annuler
      // n'est ni un refus du fournisseur ni une panne.
      else if (r.status === 403 && r.data?.error === 'STEPUP_REQUIRED') setResultat({ verdict: 'INATTENDUE', message: 'Confirmation d’identité annulée — l’essai n’a pas eu lieu.', pour: valeur })
      else setResultat({ verdict: 'INATTENDUE', message: r.data.message ?? 'L’essai n’a pas pu être lancé.', pour: valeur })
    } catch {
      setResultat({ verdict: 'INJOIGNABLE', message: 'Gestion n’a pas répondu : l’essai n’a pas eu lieu.', pour: valeur })
    } finally { setEssai('repos') }
  }

  const enregistrer = async () => {
    if (tropCourte) return
    if (verdictCourant?.verdict !== 'ACCEPTEE') {
      const ok = await confirm({
        title: 'Enregistrer une clé qui n’a pas été acceptée ?',
        message: verdictCourant
          ? `Le dernier essai de cette clé n’a pas abouti : ${verdictCourant.message} Si elle est fausse, ${etat?.nom ?? 'l’intégration'} cessera d’être lue jusqu’à ce qu’une bonne clé soit enregistrée.`
          : `Cette clé n’a pas été essayée. Si elle est fausse, ${etat?.nom ?? 'l’intégration'} cessera d’être lue jusqu’à ce qu’une bonne clé soit enregistrée. Mieux vaut cliquer d’abord sur « Tester cette clé ».`,
        variant: 'sensitive',
        confirmLabel: 'Enregistrer quand même',
      })
      if (!ok) return
    }
    setEnvoi('envoi'); setDetailEchec(null)
    try {
      const r = await adminMutate<{ error?: string; message?: string }>(
        'PUT', `/api/v1/settings/integrations/${integration}/cle`, { cle: valeur })
      if (r.status >= 200 && r.status < 300) {
        // Vidé AVANT tout le reste : le secret ne séjourne pas dans la page.
        setCle(''); setResultat(null); setEnvoi('ok')
        const t = Date.now()
        setSuivi({ depuis: t, avant: etat?.enregistree_le ?? null }); setMaintenant(t)
        charger(); onEnregistree?.()
        return
      }
      if (r.status === 403 && r.data?.error === 'STEPUP_REQUIRED') { setEnvoi('annule'); return }
      if (r.status === 403 || r.status === 401) { setEnvoi('droits'); return }
      setDetailEchec(r.data?.message ?? null); setEnvoi('echec')
    } catch { setEnvoi('echec') }
  }

  if (erreur) return <section className="lc-panel cle-panel"><div className="lc-panel__body"><p className="lc-hint">{erreur}</p></div></section>
  if (!etat) return null
  const guide = GUIDES[integration]
  const nomNouvelle = nomNouvelleCle()
  const apres = phase ? messageApresEnregistrement(phase, etat) : null

  return (
    <section className="lc-panel cle-panel" aria-labelledby={`cle-${integration}`}>
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id={`cle-${integration}`}><KeyRound size={16} aria-hidden="true" /> Clé d’accès</h2>
        <PastilleCle e={etat} />
      </header>
      <div className="lc-panel__body cle-corps">
        <p className="lc-hint cle-intro">
          Gestion lit {etat.nom} avec sa propre clé. La changer ici ne coupe rien : l’ancienne
          reste en service tant que la nouvelle n’est pas enregistrée.
        </p>

        <dl className="cle-faits">
          <div>
            <dt>Clé actuelle</dt>
            <dd>
              {!etat.configuree ? 'Aucune clé enregistrée — Gestion ne peut pas lire cette intégration.'
                : `Enregistrée le ${fmtDate(etat.enregistree_le!)}, ${etat.enregistree_par ? `par ${etat.enregistree_par.nom}` : 'par un script (personne n’est noté)'}`}
            </dd>
          </div>
          {etat.configuree ? (
            <div>
              <dt>Dernière lecture réussie</dt>
              <dd>
                {etat.reussite_depuis_enregistrement && etat.derniere_reussite
                  ? `${fraicheurFr(etat.derniere_reussite)}, avec cette clé`
                  : 'Pas encore réussie avec cette clé'}
              </dd>
            </div>
          ) : null}
          {etat.configuree && etat.enregistree_le ? (
            <div>
              <dt>Âge</dt>
              <dd>{ligneAge(etat)}</dd>
            </div>
          ) : null}
        </dl>

        {guide.avant ? (
          <p className="cle-alerte" role="note"><ShieldAlert size={15} aria-hidden="true" /> <span>{guide.avant}</span></p>
        ) : null}

        <ol className="cle-etapes">
          <li><strong>Créez une nouvelle clé.</strong> {guide.ou(etat.adresse, nomNouvelle)} L’ancienne continue de fonctionner pendant ce temps.</li>
          <li><strong>Testez-la ici.</strong> Collez-la ci-dessous puis cliquez « Tester cette clé » : après confirmation de votre identité, Gestion l’essaie chez {etat.nom} sans l’enregistrer.</li>
          <li><strong>Enregistrez-la.</strong> Gestion la relit aussitôt ; cette page indique quand une lecture a réussi avec elle.</li>
          <li><strong>Seulement ensuite :</strong> {etapeSuppression(etat.nom, nomNouvelle)} {guide.autres}</li>
        </ol>

        {peutModifier ? (
          <div className="cle-saisie">
            <label className="cle-champ">
              <span>Nouvelle clé</span>
              <input
                className="lc-input" type="password" autoComplete="new-password" spellCheck={false}
                name={`cle-${integration}`} value={cle} placeholder="Collez la nouvelle clé"
                aria-label={`Nouvelle clé ${etat.nom}`}
                onChange={(e) => { setCle(e.target.value); setEnvoi('repos'); setSuivi(null) }}
              />
            </label>
            <div className="cle-actions">
              <button type="button" className="lc-btn" onClick={() => { void tester() }}
                      disabled={tropCourte || essai === 'en_cours' || envoi === 'envoi'}>
                {essai === 'en_cours' ? 'Essai en cours…' : 'Tester cette clé'}
              </button>
              <button type="button" className="lc-btn lc-btn--primary" onClick={() => { void enregistrer() }}
                      disabled={tropCourte || essai === 'en_cours' || envoi === 'envoi'}>
                {envoi === 'envoi' ? 'Enregistrement…' : 'Enregistrer la clé'}
              </button>
            </div>
            {verdictCourant ? (
              <p role="status" className={`cle-verdict cle-verdict--${verdictCourant.verdict === 'ACCEPTEE' ? 'ok' : verdictCourant.verdict === 'REFUSEE' ? 'ko' : 'doute'}`}>
                {verdictCourant.message}
              </p>
            ) : null}
            {envoi === 'ok' && apres ? (
              <p role="status" className={`cle-verdict cle-verdict--${apres.ton}`}>{apres.texte}</p>
            ) : null}
            {envoi === 'annule' ? <p role="status" className="lc-hint">Confirmation d’identité annulée — la clé n’a pas été enregistrée.</p> : null}
            {envoi === 'droits' ? <p role="status" className="lc-hint">Vos droits ne permettent pas de remplacer cette clé.</p> : null}
            {envoi === 'echec' ? <p role="status" className="cle-verdict cle-verdict--ko">{detailEchec ?? 'L’enregistrement a échoué — l’ancienne clé reste en service.'}</p> : null}
          </div>
        ) : (
          <p className="lc-hint">Seul un compte autorisé à modifier les intégrations peut remplacer cette clé.</p>
        )}
      </div>
    </section>
  )
}
